/* eslint-disable */
import { useState, useRef } from "react";
import { useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import { Box, Button, CircularProgress, IconButton, MenuItem, Modal, TextField, Typography, styled } from "@mui/material";
import { Close } from "@mui/icons-material";
import CustomTextField from "../forms/theme-elements/CustomTextField";
import CustomSelect from "../forms/theme-elements/CustomSelect";
import CustomFormLabel from "../forms/theme-elements/CustomFormLabel";
import ParentCard from "../shared/ParentCard";
import { validateString, validateTicketDescription } from "../../utils/validations";
import { createTicket } from "../../gql/mutations/ticket";
import { convertToBase64 } from "../../utils/myUtills";
import { usePopup } from "../../context/commonPop";
import { createFetch } from "../../gql/queries/ticket";
import useQuery from "../../hooks/allQuery";

const SubmitBtn = styled(Box)(({ theme }) => ({
  marginTop: "15px",
  "& button": {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
    },
  },
}));

const ImageBox = styled(Box)({
  width: 80,
  height: 80,
  border: "2px dashed gray",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  position: "relative",
});

const categories = ["Account", "Profile", "Payment", "Website audit", "Other"];

function TicketForm({ open, setOpen }) {
  const [data, setData] = useState({
    type: "Account",
    description: "",
    images: [], 
    submitDisable: false,
  });
  const [disable, setDisable] = useState(true);
  const [helperText, setHelperText] = useState("");
  const [errorClass, setErrorClass] = useState({ type: false, description: false, images: false });
  const fileInputRefs = [useRef(null), useRef(null)]; 
  const { userData } = useSelector(state => state.reducer);
  const { handleOpen } = usePopup();
  const [CREATE_TICKET] = useMutation(createTicket, { fetchPolicy: "network-only" });
  const { allQuery } = useQuery();

  // const handleImageChange = async (file, index) => {
  //   if (!file || file.type.split("/")[0] !== "image" || file.size > 1e7) {
  //     setErrorClass(prev => ({ ...prev, images: true }));
  //     return;
  //   }
  //   const base64 = await convertToBase64(file);
  //   setData(prev => {
  //     const updatedImages = [...prev.images];
  //     updatedImages[index] = base64;
  //     return { ...prev, images: updatedImages };
  //   });
  // };

  const handleImageChange = async (file, index) => {
    if (!file || file.type.split("/")[0] !== "image" || file.size > 1e7) {
      setErrorClass(prev => ({ ...prev, images: true }));
      return;
    }
    const base64 = await convertToBase64(file);
    setData(prev => {
      const updatedImages = [...prev.images];
      updatedImages[index] = base64; 
      return { ...prev, images: updatedImages };
    });
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    if (name.startsWith("image")) {
      const index = parseInt(name.split("-")[1]);
      const selectedFiles = Array.from(files).slice(0, 2); 
      selectedFiles.forEach(async (file, i) => {
        await handleImageChange(file, index);
      });
    } else {
      setData(prev => ({ ...prev, [name]: value }));
    }
  };

  const removeImage = index => {
    setData(prev => {
      const updatedImages = [...prev.images];
      updatedImages[index] = null; // Remove image from the array
      return { ...prev, images: updatedImages };
    });
  };

  // const handleChange = async (e) => {
  //   const { name, value, files } = e.target;
  
  //   if (name.startsWith("image")) {
  //     const index = parseInt(name.split("-")[1]); 
  //     const selectedFiles = Array.from(files).slice(0, 2);
    
  //     selectedFiles.forEach(async (file, i) => {
  //       await handleImageChange(file, i);
  //     });
  
  //   } else {
  //     setData(prev => ({ ...prev, [name]: value }));
  //   }
  // };
  
  const checkError = item => {
    const typeError = validateString(item.type);
    const descriptionError = validateTicketDescription(item.description);
    setErrorClass({ type: typeError, description: descriptionError, images: errorClass.images });
    return typeError || descriptionError || errorClass.images;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (checkError(data)) return;
    setData(prev => ({ ...prev, submitDisable: true }));

    allQuery({
      QUERY: CREATE_TICKET,
      variables: {
        createData: {
          description: data.description,
          type: data.type,
          userId: userData.id,
          images: data.images.filter(Boolean),
          name: userData.name,
        },
      },
      type: "create",
      cacheQueryData: {
        cacheQuery: createFetch,
        variablesCache: { userId: userData.id },
      },
      onCompleteFunc: () => {
        handleOpen(true, "✔ Ticket created successfully!");
        setData({ description: "", images: [], type: "Account", submitDisable: false });
        setOpen(false);
      },
      onErrorFunc: ({ message }) => {
        handleOpen(true, message);
        setData({ description: "", images: [], type: "Account", submitDisable: false });
        setOpen(false);
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        setData({ description: "", images: [], type: "Account" });
        setOpen(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <ParentCard boxHeight="boxHeight" title="Ticket Creation Form">
        <Box component="form" onSubmit={handleSubmit}>
          <CustomFormLabel>Type</CustomFormLabel>
          <CustomSelect fullWidth name="type" value={data.type} onChange={handleChange}>
            {categories.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </CustomSelect>
          <Box className="descriptionTicket">
            <CustomFormLabel>Description</CustomFormLabel>
            <CustomTextField
              name="description"
              multiline
              rows={4}
              fullWidth
              value={data.description}
              onChange={e => {
                let value = e.target.value.replace(/[^a-zA-Z0-9\s@_]/g, "");
                const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1);

                if (/^\d+$/.test(e.target.value)) {
                  setErrorClass(prev => ({ ...prev, description: true }));
                  setHelperText("The description must contain meaningful text, not just numbers.");
                  return;
                }

                if (capitalizedValue.trim().length > 5) {
                  setDisable(false);
                } else {
                  setDisable(true);
                }

                if (capitalizedValue.trim().length < 5 || capitalizedValue.trim().length > 300) {
                  setErrorClass(prev => ({ ...prev, description: true }));
                  setHelperText("Description must be between 5-300 characters");
                } else {
                  setErrorClass(prev => ({ ...prev, description: false }));
                  setHelperText("");
                }

                if (capitalizedValue.trim().length <= 300) {
                  handleChange({ target: { name: "description", value: capitalizedValue } });
                }
              }}
              onPaste={e => {
                e.preventDefault();
                let pastedText = e.clipboardData.getData("text").slice(0, 300);
                pastedText = pastedText.replace(/[^a-zA-Z0-9\s@_]/g, "");
                const capitalizedText = pastedText.charAt(0).toUpperCase() + pastedText.slice(1);

                if (/^\d+$/.test(pastedText)) {
                  setErrorClass(prev => ({ ...prev, description: true }));
                  setHelperText("The description must contain meaningful text, not just numbers.");
                  return;
                }

                if (capitalizedText.trim().length < 5 || capitalizedText.trim().length > 300) {
                  setErrorClass(prev => ({ ...prev, description: true }));
                  setHelperText("Description must be between 5-300 characters");
                } else {
                  setErrorClass(prev => ({ ...prev, description: false }));
                  setHelperText("");
                }

                handleChange({ target: { name: "description", value: capitalizedText } });
              }}
              error={errorClass.description}
              helperText={helperText}
            />
          </Box>

          {data?.description?.trim()?.length > 0 && (
            <Box className="MaximumCharacters">
              <Typography component="h3">Maximum Characters: </Typography>
              <Typography component="p">{300 - data?.description?.trim()?.length}/300</Typography>
            </Box>
          )}

          {/* <CustomFormLabel>Description</CustomFormLabel>
          <CustomTextField
            name="description"
            multiline
            rows={4}
            fullWidth
            value={data.description}
            onChange={handleChange}
            error={errorClass.description}
            helperText={errorClass.description && "Description required (5-300 characters)."}
          /> */}

          <CustomFormLabel>Images (JPG, PNG | Max: 10MB each)</CustomFormLabel>
          <Box display="flex" gap={2}>
            {[0, 1].map(index => (
              <Box key={index} display="flex" flexDirection="column" alignItems="center">
                <input ref={fileInputRefs[index]} type="file" multiple name={`image-${index}`} accept="image/*" hidden onChange={handleChange} value={""} />
                <ImageBox onClick={() => !data.images[index] && fileInputRefs[index].current.click()} sx={{ borderColor: data.images[index] ? "transparent" : "gray" }}>
                  {data.images[index] ? (
                    <>
                      <img src={data.images[index]} alt={`Preview ${index}`} width="100%" height="100%" style={{ objectFit: "cover", borderRadius: 4 }} />
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          removeImage(index);
                        }}
                        sx={{ position: "absolute", top: 5, right: 5, backgroundColor: "white" }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Typography variant="body2">+ Select</Typography>
                  )}
                </ImageBox>
              </Box>
            ))}
          </Box>

          <SubmitBtn>
            <Button variant="contained" type="submit" disabled={data.submitDisable || disable}>
              {data.submitDisable ? <CircularProgress size={24} /> : "Submit"}
            </Button>
          </SubmitBtn>
        </Box>
      </ParentCard>
    </Modal>
  );
}

export default TicketForm;
